import { FC, useId } from 'react';
import { IonPopover } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { InfoIcon } from '../theme/Icons';
import { BRAND, CHART_INK } from './insights/agencyChartTheme';
import { IOrganizationUser } from './IOrganization';

const useStyles = createUseStyles({
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        flexShrink: 0,
        padding: 0,
        background: 'none',
        border: 0,
        font: 'inherit',
        color: BRAND.coralInk,
        cursor: 'pointer'
    },

    popover: {
        '--max-width': '280px',
        '--width': '280px'
    },

    body: {
        padding: '12px 14px',
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.strongLabel
    }
});

const listNames = (members: IOrganizationUser[]): string => {
    const names = members.map(member => `${member.firstName} ${member.lastName}`.trim());
    if (names.length <= 1) {
        return names.join('');
    }
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * Why a team member can see a client's name but little else, behind a mark beside their row. Who
 * holds the client decides who to ask: the colleagues in `heldBy`, or with nobody on the team holding
 * them, the client.
 *
 * A standing fact about access rather than news: stated in full under every care map, it crowded
 * the list it was explaining. A button rather than a hover target, so a touch screen and a keyboard
 * can both reach it.
 */
export const ClientAccessTip: FC<{ heldBy: IOrganizationUser[] }> = ({ heldBy }) => {
    const classes = useStyles();
    const triggerId = `client-access-${useId()}`;

    return (
        <>
            <button
                id={triggerId}
                type="button"
                className={classes.button}
                data-id="client-access-tip"
                aria-label="Why this client's details are limited"
            >
                <InfoIcon color="inherit" />
            </button>

            <IonPopover
                className={classes.popover}
                trigger={triggerId}
                triggerAction="click"
                side="bottom"
                alignment="end"
                showBackdrop={false}
                dismissOnSelect
            >
                {heldBy.length > 0 ? (
                    <div className={classes.body} data-id="client-access-tip-body">
                        This client's profile is shared with <span className="mp-mask">{listNames(heldBy)}</span>,
                        not with you, so you can see the client's name and little else until it is shared with you.
                    </div>
                ) : (
                    <div className={classes.body} data-id="client-access-tip-body">
                        This existing Innerhive profile is not shared with your organization, so your access is very
                        limited. Request access from the client to gain full access.
                    </div>
                )}
            </IonPopover>
        </>
    );
};
